/**
 * Auth0 Configuration
 * Centralizes all Auth0-related settings and provides utility functions
 */

// Auth0 tenant configuration
export const AUTH0_CONFIG = {
  domain: process.env.NEXT_PUBLIC_AUTH0_DOMAIN || "",
  clientId: process.env.NEXT_PUBLIC_AUTH0_CLIENT_ID || "",
  authorizationParams: {
    redirect_uri:
      typeof window !== "undefined" ? `${window.location.origin}/callback` : "",
    audience:
      process.env.NEXT_PUBLIC_AUTH0_AUDIENCE ||
      "https://www.spindoctor.app/api",
    scope: "openid profile email offline_access",
  },
  useRefreshTokens: true,
  cacheLocation: "localstorage" as const,
};

// Protected routes that require authentication
export const PROTECTED_ROUTES = ["/profile", "/disputes", "/subscriptions"];

// Auth paths for redirect prevention (to avoid login loops)
export const AUTH_PATHS = ["/callback", "/login", "/logout"];

/**
 * Check if a path requires authentication
 */
export function isProtectedPath(path: string): boolean {
  return PROTECTED_ROUTES.some(
    (protectedPath) =>
      path === protectedPath || path.startsWith(`${protectedPath}/`)
  );
}

/**
 * Check if the current path is related to authentication
 * Used to prevent redirect loops during auth flows
 */
export function isAuthPath(path?: string): boolean {
  if (!path && typeof window === "undefined") return false;

  const currentPath =
    path || (typeof window !== "undefined" ? window.location.pathname : "");
  return AUTH_PATHS.some((authPath) => currentPath.startsWith(authPath));
}

/**
 * Get login URL with optional return path
 */
export function getLoginUrl(returnTo?: string): string {
  if (!returnTo || typeof window === "undefined") {
    return "/login";
  }

  return `/login?returnTo=${encodeURIComponent(returnTo)}`;
}

/**
 * Get API base URL depending on environment
 */
export function getApiBaseUrl(): string {
  // First check for explicit API URL in env variables
  const configuredApiUrl = process.env.NEXT_PUBLIC_API_BASE_URL;
  if (configuredApiUrl) {
    return configuredApiUrl;
  }

  // For client-side, construct API URL from origin
  if (typeof window !== "undefined") {
    // If running locally, default to localhost:8080 for API
    if (window.location.hostname === "localhost") {
      return "http://localhost:8080";
    }
    // Otherwise, return just the origin
    return window.location.origin;
  }

  // Server-side fallback
  return "http://localhost:8080";
}
